import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Phone } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import ProgramIcon from "@/components/ProgramIcon";
import { IMAGES, PERIL_META, SITE } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";
import { usePageMeta } from "@/lib/usePageMeta";


function PropertiesCarousel({ alt }) {
  const images = [IMAGES.properties2, IMAGES.whiteBuilding, IMAGES.industrialPark];
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % images.length), 4500);
    return () => clearInterval(id);
  }, [images.length]);
  return (
    <div className="relative h-[380px] overflow-hidden shadow-xl lg:h-[460px]" data-testid="properties-carousel">
      {images.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={i === index ? alt : ""}
          aria-hidden={i !== index}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${i === index ? "opacity-100" : "opacity-0"}`}
          loading={i === 0 ? "eager" : "lazy"}
          data-testid={`properties-image-${i}`}
        />
      ))}
      <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-2">
        {images.map((src, i) => (
          <button
            key={src}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`Show property image ${i + 1}`}
            aria-pressed={i === index}
            className={`h-2.5 w-2.5 rounded-full transition-colors duration-300 ${i === index ? "bg-white" : "bg-white/40 hover:bg-white/70"}`}
            data-testid={`properties-carousel-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Properties() {
  const { t } = useLang();
  usePageMeta(t("meta.properties")[0], t("meta.properties")[1]);
  const perils = t("propertiesPage.perils");
  const coverage = t("propertiesPage.coverage");
  return (
    <>
      <PageHero
        overline={t("propertiesPage.heroOverline")}
        title={t("propertiesPage.heroTitle")}
        sub={t("propertiesPage.heroSub")}
        testId="properties"
      />

      <section className="bg-white py-16 sm:py-24" aria-labelledby="properties-detail-heading" data-testid="properties-detail-section">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <div>
            <Reveal>
              <h2 id="properties-detail-heading" className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                {t("propertiesPage.detailHeading")}
              </h2>
            </Reveal>
            <Reveal delay={1}>
              <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg" data-testid="properties-intro-copy">
                {t("propertiesPage.intro")}
              </p>
            </Reveal>
            <Reveal delay={2}>
              <h3 className="font-heading mt-8 text-sm font-bold uppercase tracking-[0.2em] text-[#0F4C5C]">
                {t("propertiesPage.perilsHeading")}
              </h3>
            </Reveal>
            <ul className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="perils-grid">
              {perils.map((peril, i) => (
                <Reveal key={peril} delay={i % 3} as="li">
                  <span className="flex h-full flex-col items-start gap-2 border border-[#0F4C5C]/10 bg-[#EEF6FC] p-4" data-testid={`peril-${i}`}>
                    <ProgramIcon name={PERIL_META[i]?.icon} className="h-5 w-5 text-[#0F4C5C]" />
                    <span className="text-sm font-semibold text-slate-800">{peril}</span>
                  </span>
                </Reveal>
              ))}
            </ul>
            <Reveal delay={2}>
              <h3 className="font-heading mt-10 text-sm font-bold uppercase tracking-[0.2em] text-[#0F4C5C]">{t("propertiesPage.coverageHeading")}</h3>
              <ul className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2" data-testid="coverage-list">
                {coverage.map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm font-medium text-slate-700">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-[#D97706]" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={1}>
            <div className="lg:sticky lg:top-32">
              <PropertiesCarousel alt={t("propertiesPage.imageAlt")} />
              <div className="mt-6 bg-[#0F4C5C] p-7 text-white" data-testid="properties-cta-card">
                <h3 className="font-heading text-xl font-bold">{t("propertiesPage.ctaTitle")}</h3>
                <p className="mt-2 text-sm text-slate-300">
                  {t("propertiesPage.ctaSub")}
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link to="/quote" className="inline-flex items-center gap-2 bg-[#D97706] px-5 py-2.5 text-sm font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[#B45309]" data-testid="properties-quote-btn">
                    {t("common.requestQuote")} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                  <a href={SITE.phone1Href} className="inline-flex items-center gap-2 border border-white/40 px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-white/10" data-testid="properties-call-btn">
                    <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phone1}
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
